// ============================================================
// 👥 Party 模型 — 多人队伍管理
// ============================================================

import { Character } from './character.js';

export class Party {
  constructor(members = []) {
    this.members = members.map(m => m instanceof Character ? m : new Character(m));
    this.activeIndex = 0;       // 当前行动中的队员索引
    this.turnOrder = [];         // [0, 1, 2, ...] 本轮行动顺序
    this.round = 0;              // 当前回合数
  }

  // ── 当前角色 ──
  get current() { return this.members[this.activeIndex]; }

  // ── 存活成员数 ──
  get aliveCount() { return this.members.filter(m => m.isAlive).length; }

  // ── 所有成员存活 ──
  get allAlive() { return this.members.every(m => m.isAlive); }

  // ── 全灭 ──
  get allDead() { return this.members.every(m => !m.isAlive); }

  // ── 指定索引成员 ──
  get(index) { return this.members[index]; }

  // ── 初始化回合 ──
  startRound() {
    this.round++;
    // 按速度排序决定行动顺序
    const indexed = this.members
      .map((m, i) => ({ index: i, spd: m.isAlive ? m.getEffSpd() : -1 }))
      .filter(x => x.spd >= 0)
      .sort((a, b) => b.spd - a.spd);
    this.turnOrder = indexed.map(x => x.index);
    this.activeIndex = this.turnOrder[0] || 0;
  }

  // ── 推进到下一个存活成员 ──
  nextTurn() {
    if (this.turnOrder.length === 0) return false;
    const currentPos = this.turnOrder.indexOf(this.activeIndex);
    let nextPos = currentPos + 1;

    // 跳过已死亡的成员
    while (nextPos < this.turnOrder.length) {
      const idx = this.turnOrder[nextPos];
      if (this.members[idx].isAlive) {
        this.activeIndex = idx;
        return true;
      }
      nextPos++;
    }

    // 所有成员都已行动 → 回合结束
    return false;
  }

  // ── 所有存活成员是否都行动完 ──
  get roundComplete() {
    if (this.turnOrder.length === 0) return true;
    const currentPos = this.turnOrder.indexOf(this.activeIndex);
    if (currentPos < 0) return true;
    // 从当前位置往后，还有没有存活的成员？
    for (let i = currentPos + 1; i < this.turnOrder.length; i++) {
      if (this.members[this.turnOrder[i]].isAlive) return false;
    }
    return true;
  }

  // ── 对全队应用效果（AOE伤害/全体治疗） ──
  applyToAll(fn) {
    this.members.forEach((m, i) => { if (m.isAlive) fn(m, i); });
  }

  // ── 序列化 ──
  toJSON() {
    return {
      members: this.members.map(m => m.toJSON()),
      activeIndex: this.activeIndex,
      turnOrder: this.turnOrder,
      round: this.round,
    };
  }

  // ── 从 JSON 反序列化 ──
  static fromJSON(data) {
    if (!data || !data.members) return null;
    const party = new Party(data.members);
    party.activeIndex = data.activeIndex || 0;
    party.turnOrder = data.turnOrder || [];
    party.round = data.round || 0;
    return party;
  }

  // ── 从 S 创建单人队伍 ──
  static fromSolo(player) {
    const char = player instanceof Character ? player : new Character(player);
    const party = new Party([char]);
    party.activeIndex = 0;
    party.turnOrder = [0];
    return party;
  }
}
